<template>
    <div class="vrm-modal vrm-modal--wide mfp-with-anim">
        <div class="vrm-modal__header">
            <div class="vrm-modal__title">
                {{ $t('teams.link_site_groups', { i18nFallback: 'Link installation groups' }) }}
            </div>
        </div>
        <form class="vrm-modal__content" @submit.prevent="onSubmit" @reset.prevent="onReset">
            <fieldset class="vrm-fieldset">
                <div class="vrm-form__fieldset-item">
                    <table class="vrm-table vrm-table--form is-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('teams.group', { i18nFallback: 'Group' }) }}</th>
                                <th>{{ $t('users.role', { i18nFallback: 'Role' }) }}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="index">
                                <td class="vrm-table__td--fill-out">
                                    <vrm-dropdown
                                        v-model="item.siteGroup"
                                        :options="groups"
                                        search-enabled
                                    />
                                </td>
                                <td>
                                    <vrm-dropdown
                                        v-model="item.accessLevelOption"
                                        :options="accessLevelDropdownOptions"
                                    />
                                </td>
                                <td class="vrm-table__actions">
                                    <a
                                        v-if="item.siteGroup"
                                        class="vrm-table__icon--link"
                                        @click.prevent="items.splice(index, 1)"
                                    >
                                        <i class="sg-vrm-icon_059_min-round-16px" />
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </fieldset>
            <fieldset class="vrm-fieldset">
                <div class="sg-spread">
                    <button type="reset" class="sg-btn sg-site" :disabled="busy">
                        {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                    </button>
                    <button
                        type="submit"
                        class="sg-btn sg-blue"
                        :class="{ 'sg-disabled': !canSubmit }"
                        :disabled="busy || !canSubmit"
                    >
                        {{
                            $t('teams.add_site_groups', { i18nFallback: 'Add installation groups' })
                        }}
                    </button>
                </div>
            </fieldset>
        </form>
        <modal-close-handle />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ngInject } from '@/vue/decorators/angular-interop';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import {
    accessLevelDropdownOptions,
    findAccessLevelOption,
    vrmInstallationAccessLevel,
} from '@/vue/store/modules/installation-access-level';

@Component({
    components: { VrmToggle, ModalCloseHandle, VrmDropdown },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('teams')
@AngularLocaleBundle('users')
export default class UserGroupLinkInstallationGroupsModal extends Vue {
    @Prop({ type: Object })
    team;

    /** @type InstallationGroupsService */
    @ngInject()
    InstallationGroupsService;
    /** @type UserGroupsService */
    @ngInject()
    UserGroupsService;
    @ngInject()
    ApiService;

    /**
     * @type {Array<{siteGroup, accessLevelOption}>}
     */
    items = [];
    busy = false;
    accessLevelDropdownOptions = accessLevelDropdownOptions;

    get groups() {
        const groups = this.$store.state.InstallationGroupsService.groups || [];
        const taken = new Set(
            this.items.map(item => (item.userGroup ? item.userGroup.idSiteGroup : 0)),
        );
        if (this.team) {
            this.team.siteGroups.forEach(userGroup => taken.add(userGroup.idSiteGroup));
        }
        return groups.filter(s => !taken.has(s.idSiteGroup));
    }

    created() {
        this.InstallationGroupsService.loadInstallationGroups();
    }

    get lastBox() {
        if (!this.items.length) return null;
        return this.items[this.items.length - 1].siteGroup;
    }

    @Watch('lastBox', { immediate: true })
    onLastBoxFilled(value) {
        if ((value !== null && this.groups.length) || !this.items.length) {
            this.items.push({
                siteGroup: null,
                accessLevelOption: findAccessLevelOption(vrmInstallationAccessLevel.MONITORING),
            });
        }
    }

    get canSubmit() {
        return this.items.some(item => item.siteGroup);
    }

    async onSubmit() {
        if (this.busy) return;
        this.busy = true;
        try {
            const payload = this.items
                .filter(item => item.siteGroup)
                .map(item => ({
                    idSiteGroup: item.siteGroup.idSiteGroup,
                    accessLevel: item.accessLevelOption.accessLevel,
                }));
            await this.UserGroupsService.linkSiteGroups(this.team.idUserGroup, payload);
            this.$emit('close');
        } finally {
            this.busy = false;
        }
    }

    onReset() {
        if (this.busy) return;
        this.$emit('close');
    }
}
</script>
